import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import {
  Button,
  Divider,
  Header,
  Icon,
  Input,
  Item,
  Popup,
  Responsive,
} from "semantic-ui-react";

import { SetHub } from "data/hub/actions";
import { CreateStream } from "data/libs/streams";

import { SetAlert } from "components/Alerts/actions";
import { ToggleStreamModal } from "components/StreamModal/actions";
import colors from "components/StreamModal/colors";

class ManageStreams extends React.Component {
  static propTypes = {
    hub: PropTypes.shape({
      id: PropTypes.string.isRequired,
      streams: PropTypes.array.isRequired,
    }).isRequired,
    setAlert: PropTypes.func.isRequired,
    setHub: PropTypes.func.isRequired,
    toggleStreamModal: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    // Set the color set to any color that hasn't been chosen
    const usedColors = props.hub.streams.map((stream) => {
      return stream.color;
    });
    const colorSet = colors.filter((color) => {
      return !usedColors.includes(color);
    });
    this.state = {
      dropdownOpen: false,
      loading: false,
      streamSearch: "",
      colorSet: colorSet.length === 0 ? [...colors] : colorSet,
    };
  }

  toggleDropdown = () => {
    this.setState((state) => {
      return { dropdownOpen: !state.dropdownOpen };
    });
  };

  closeDropdown = (event) => {
    // Get undefined event when clicking outside of the dropdown, close in this case
    if (!event) {
      this.setState({ dropdownOpen: false });
    }
  };

  getDropdownOptions = () => {
    const { streamSearch } = this.state;
    const { hub } = this.props;
    const dropdownOptions = [];
    hub.streams.sort((a, b) => {
      return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
    });
    hub.streams.forEach((stream) => {
      // Only add if the search is empty or matches the current search
      if (
        !streamSearch ||
        stream.name.toLowerCase().includes(streamSearch.toLowerCase())
      ) {
        dropdownOptions.push({
          text: stream.name,
          key: stream.id,
          value: stream,
          style: {
            backgroundColor: `${stream.color}`,
            color: "white",
          },
        });
      }
    });
    return dropdownOptions;
  };

  addStream = () => {
    const { colorSet, streamSearch } = this.state;
    const { hub, setHub, setAlert } = this.props;
    if (!streamSearch.trim()) {
      setAlert({
        type: "warning",
        text: "Input field cannot be empty. Please verify stream name and try again.",
      });
      return;
    }

    // Check if stream already exists, if so, just add client to stream
    const streamExists = hub.streams.find((stream) => {
      return stream.name === streamSearch;
    });
    if (streamExists) {
      setAlert({
        type: "warning",
        text: `Stream ${streamSearch} already exists. Please verify stream name and try again.`,
      });
      return;
    }

    const hubCopy = { ...hub };

    // Pick a random color that hasn't been chosen yet
    let currentColorSet = colorSet;
    const randomIndex = Math.floor(Math.random() * currentColorSet.length);
    const nextColor = currentColorSet[randomIndex];
    currentColorSet.splice(randomIndex, 1);
    // If we used up all the colors, allow any color to be chosen
    if (currentColorSet.length <= 0) {
      currentColorSet = [...colors];
    }

    const request = {
      hubId: hub.id,
      name: streamSearch,
      color: nextColor,
    };
    this.setState({ colorSet: currentColorSet, loading: true });
    CreateStream(request)
      .then((response) => {
        hubCopy.streams.push(response);
        setHub(hubCopy);
        this.setState({ loading: false, streamSearch: "" });
      })
      .catch((error) => {
        setAlert({ type: "error", text: `Failed to create stream.` });
        this.setState({ loading: false });
      });
  };

  handleKeyUp = (e) => {
    // handles key press for adding streams
    if (e.keyCode === 13) {
      // Enter pressed
      this.addStream();
    }
  };

  render() {
    const { dropdownOpen, loading, streamSearch } = this.state;
    const { hub, toggleStreamModal } = this.props;
    const dropdownOptions = this.getDropdownOptions();
    hub.streams.sort((a, b) => {
      return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
    });

    return (
      <div style={{ flex: "0 1 auto", margin: "0" }}>
        <div style={{ marginRight: "0.5em" }}>
          <Popup
            data-test="stream-popup"
            icon // required to not have default dropdown icon and not show space
            open={dropdownOpen}
            onClose={() => {
              this.closeDropdown();
            }}
            position="top right"
            style={{ paddingLeft: 0, paddingRight: 0 }}
            trigger={
              <React.Fragment>
                <Responsive maxWidth={1350}>
                  <Button basic icon onClick={this.toggleDropdown}>
                    <Icon.Group>
                      <Icon name="align justify" />
                      <Icon corner name="cog" />
                    </Icon.Group>
                  </Button>
                </Responsive>
                <Responsive minWidth={1351} style={{ whiteSpace: "nowrap" }}>
                  <Button
                    basic
                    onClick={this.toggleDropdown}
                    data-test="hub-menu-streams-button"
                  >
                    <Icon.Group style={{ marginRight: "0.428571em" }}>
                      <Icon name="align justify" />
                      <Icon corner name="cog" />
                    </Icon.Group>
                    Streams
                  </Button>
                </Responsive>
              </React.Fragment>
            }
          >
            <Header as="h6" content="Streams" style={{ paddingLeft: "1em" }} />
            <Divider style={{ marginBottom: "0" }} />
            <Item key="add-stream" style={{ padding: "0.5em 1em" }}>
              <div style={{ display: "flex" }}>
                <Input
                  autoComplete="off"
                  onChange={(e, data) => {
                    this.setState({ streamSearch: data.value });
                  }}
                  onKeyUp={this.handleKeyUp}
                  placeholder="Add a Stream"
                  value={streamSearch}
                />
                <div>
                  <Button
                    disabled={loading}
                    loading={loading}
                    icon="plus"
                    style={{ marginLeft: "0.25em", marginRight: 0 }}
                    onClick={this.addStream}
                  />
                </div>
              </div>
            </Item>
            <Divider style={{ marginBottom: "0", marginTop: "0em" }} />
            <div style={{ maxHeight: "300px", overflowY: "scroll" }}>
              {dropdownOptions.map((option) => {
                return (
                  <Item
                    data-test="stream-item"
                    className="grey_on_hover"
                    key={option.key}
                    style={{ padding: ".5em 1em" }}
                    onClick={() => {
                      toggleStreamModal(option.value);
                      this.toggleDropdown();
                    }}
                  >
                    <Button fluid style={{ ...option.style }}>
                      {option.text}
                    </Button>
                  </Item>
                );
              })}
            </div>
          </Popup>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    hub: state.hubly.data.hub.selected.hub,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setAlert: (alert) => {
      dispatch(SetAlert(alert));
    },
    setHub: (hub) => {
      dispatch(SetHub(hub));
    },
    toggleStreamModal: (stream) => {
      dispatch(ToggleStreamModal(stream, true));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageStreams);
